/**
 * File Expiration Utilities
 * Handles automatic file expiration after 1 year
 */

export const FILE_EXPIRATION_DAYS = 365; // 1 year

export interface ExpirationInfo {
  isExpired: boolean;
  daysRemaining: number;
  expirationDate: Date;
  createdDate: Date;
  isNearExpiration: boolean; // < 30 days
  isCritical: boolean; // < 7 days
  percentageRemaining: number; // 0-100
}

/**
 * Calculate expiration info for a file
 */
export const getFileExpirationInfo = (createdAt: string | undefined): ExpirationInfo | null => {
  if (!createdAt) {
    return null;
  }

  const createdDate = new Date(createdAt);
  const now = new Date();
  const expirationDate = new Date(createdDate);
  expirationDate.setDate(expirationDate.getDate() + FILE_EXPIRATION_DAYS);

  const msRemaining = expirationDate.getTime() - now.getTime();
  const daysRemaining = Math.ceil(msRemaining / (1000 * 60 * 60 * 24));
  const isExpired = daysRemaining <= 0;
  const isNearExpiration = daysRemaining <= 30 && daysRemaining > 7;
  const isCritical = daysRemaining <= 7 && daysRemaining > 0;
  
  const totalDays = FILE_EXPIRATION_DAYS;
  const daysPassed = totalDays - daysRemaining;
  const percentageRemaining = Math.max(0, Math.min(100, (daysRemaining / totalDays) * 100));

  return {
    isExpired,
    daysRemaining,
    expirationDate,
    createdDate,
    isNearExpiration,
    isCritical,
    percentageRemaining
  };
};

/**
 * Format time remaining as human-readable string
 */
export const formatTimeRemaining = (daysRemaining: number): string => {
  if (daysRemaining <= 0) {
    return 'Expired';
  }

  if (daysRemaining === 1) {
    return '1 day';
  }

  if (daysRemaining < 30) {
    return `${daysRemaining} days`;
  }

  const months = Math.floor(daysRemaining / 30);
  const remainingDays = daysRemaining % 30;

  if (months === 1) {
    if (remainingDays === 0) return '1 month';
    return `1 month, ${remainingDays} days`;
  }

  if (remainingDays === 0) {
    return `${months} months`;
  }

  return `${months} months, ${remainingDays} days`;
};

/**
 * Get color class based on expiration status
 */
export const getExpirationColorClass = (expiration: ExpirationInfo): string => {
  if (expiration.isExpired) {
    return 'text-red-600 bg-red-50';
  }
  if (expiration.isCritical) {
    return 'text-orange-600 bg-orange-50';
  }
  if (expiration.isNearExpiration) {
    return 'text-yellow-600 bg-yellow-50';
  }
  return 'text-gray-500 bg-gray-50';
};

/**
 * Get icon color for expiration badge
 */
export const getExpirationIconColor = (expiration: ExpirationInfo): string => {
  if (expiration.isExpired) {
    return 'text-red-600';
  }
  if (expiration.isCritical) {
    return 'text-orange-600';
  }
  if (expiration.isNearExpiration) {
    return 'text-yellow-600';
  }
  return 'text-gray-400';
};

/**
 * Get progress bar color based on percentage remaining
 */
export const getProgressBarColor = (percentageRemaining: number): string => {
  if (percentageRemaining <= 0) {
    return 'bg-red-500';
  }
  if (percentageRemaining <= 10) {
    return 'bg-orange-500';
  }
  if (percentageRemaining <= 25) {
    return 'bg-yellow-500';
  }
  return 'bg-green-500';
};

/**
 * Check if a file should be automatically deleted
 */
export const shouldAutoDelete = (createdAt: string | undefined): boolean => {
  const expiration = getFileExpirationInfo(createdAt);
  return expiration?.isExpired ?? false;
};

/**
 * Filter out expired files from an array
 */
export const filterExpiredFiles = <T extends { createdAt?: string }>(files: T[]): T[] => {
  return files.filter(file => !shouldAutoDelete(file.createdAt));
};

/**
 * Get list of expired files
 */
export const getExpiredFiles = <T extends { createdAt?: string; name?: string }>(files: T[]): T[] => {
  return files.filter(file => shouldAutoDelete(file.createdAt));
};
